"use client";

import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
} from "react";
import { cn } from "@/lib/utils";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { type Translate, useViewerLanguage } from "@/lib/i18n";
import type {
  KeywordChipVariant,
  KeywordGroup,
  KeywordItem,
  KeywordWeight,
} from "@/types/modules";

export function keywordWeightLabels(t: Translate): Record<KeywordWeight, string> {
  return {
    3: t("Core"),
    2: t("Important"),
    1: t("Mentioned"),
  };
}

export const keywordWeights: KeywordWeight[] = [3, 2, 1];

export type KeywordDimension = "group" | "weight";

export function keywordDimensionLabels(t: Translate): Record<KeywordDimension, string> {
  return {
    group: t("Group"),
    weight: t("Weight"),
  };
}

export type NormalizedKeyword = KeywordItem & {
  id: string;
  weight: KeywordWeight;
};

export interface KeywordBucket {
  id: string;
  label: string;
  weight?: KeywordWeight;
  items: NormalizedKeyword[];
}

export function buildKeywordBuckets(
  list: NormalizedKeyword[],
  dimension: KeywordDimension,
  groups: KeywordGroup[],
  otherLabel: string,
  weightLabels: Record<KeywordWeight, string>,
): KeywordBucket[] {
  if (dimension === "weight") {
    return keywordWeights
      .map((w) => ({
        id: String(w),
        label: weightLabels[w],
        weight: w,
        items: list.filter((k) => k.weight === w),
      }))
      .filter((b) => b.items.length > 0);
  }

  const known = new Set(groups.map((g) => g.id));
  const buckets: KeywordBucket[] = groups
    .map((g) => ({
      id: g.id,
      label: g.label,
      items: list.filter((k) => k.group === g.id),
    }))
    .filter((b) => b.items.length > 0);
  const rest = list.filter((k) => !k.group || !known.has(k.group));
  if (rest.length) {
    buckets.push({ id: "__other__", label: otherLabel, items: rest });
  }
  return buckets;
}

export function keywordGroupColors(
  groups: KeywordGroup[],
  palette: string[],
): Record<string, string> {
  return Object.fromEntries(
    groups.map((g, i) => [g.id, palette[i % palette.length]]),
  );
}

export function resolveKeywordColor(
  keyword: NormalizedKeyword,
  groupColors: Record<string, string>,
): string | undefined {
  return keyword.color ?? (keyword.group ? groupColors[keyword.group] : undefined);
}

export function availableKeywordDimensions(
  list: NormalizedKeyword[],
  groups: KeywordGroup[],
  otherLabel: string,
  weightLabels: Record<KeywordWeight, string>,
): KeywordDimension[] {
  return (["group", "weight"] as KeywordDimension[]).filter(
    (d) => buildKeywordBuckets(list, d, groups, otherLabel, weightLabels).length > 1,
  );
}

export type ChipState = "hover" | null;

export function variantFromWeight(weight: KeywordWeight): KeywordChipVariant {
  if (weight === 3) return "ghost";
  if (weight === 2) return "outline";
  return "dashed";
}

interface ChipStyle {
  className: string;
  style?: CSSProperties;
}

export function keywordChipStyle(
  weight: KeywordWeight,
  state: ChipState = null,
  variantOverride?: KeywordChipVariant,
  color?: string,
): ChipStyle {
  const variant = variantOverride ?? variantFromWeight(weight);
  const hovered = state === "hover";

  if (color) {
    if (variant === "ghost") {
      return {
        className: cn(
          "border font-medium text-[var(--fg)]",
          hovered && "brightness-110",
        ),
        style: {
          borderColor: color,
          backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
        },
      };
    }
    if (variant === "outline") {
      return {
        className: cn(
          "border text-[var(--fg)]",
          hovered && "brightness-110",
        ),
        style: { borderColor: color },
      };
    }
    return {
      className: cn(
        "border border-dashed text-[var(--muted)]",
        hovered && "brightness-110",
      ),
      style: { borderColor: color },
    };
  }

  if (variant === "ghost") {
    return {
      className: cn(
        "border font-medium text-[var(--fg)]",
        hovered
          ? "border-[var(--border-strong)] bg-[var(--track)]"
          : "border-[var(--border-strong)] bg-[var(--track)]",
      ),
    };
  }
  if (variant === "outline") {
    return {
      className: cn(
        "border text-[var(--fg)]",
        hovered
          ? "border-[var(--border-strong)] bg-[var(--track)]"
          : "border-[var(--border-strong)]",
      ),
    };
  }
  return {
    className: cn(
      "border border-dashed text-[var(--muted)]",
      hovered
        ? "border-[var(--border-strong)] bg-[var(--track)]"
        : "border-[var(--border-strong)]",
    ),
  };
}

export function normalizeKeywords(items: KeywordItem[]): NormalizedKeyword[] {
  return items.map((k, i) => ({
    ...k,
    id: k.id ?? `kw-${i}`,
    weight: k.weight ?? 2,
  }));
}

export interface KeywordChipProps {
  text: string;
  weight: KeywordWeight;
  variant?: KeywordChipVariant;
  color?: string;
  state?: ChipState;
  dim?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  ariaPressed?: boolean;
}

export function KeywordChip({
  text,
  weight,
  variant,
  color,
  state = null,
  dim,
  onMouseEnter,
  onMouseLeave,
  onClick,
  ariaPressed,
}: KeywordChipProps) {
  const tone = keywordChipStyle(weight, state, variant, color);
  const hasHover = useHoverCapable();
  return (
    <button
      type="button"
      aria-pressed={ariaPressed}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={hasHover ? onMouseEnter : undefined}
      onBlur={hasHover ? onMouseLeave : undefined}
      onClick={onClick}
      style={tone.style}
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-md px-2.5 py-1 text-sm transition-all",
        tone.className,
        dim && "opacity-30",
      )}
    >
      {text}
    </button>
  );
}

export function KeywordChipVisual({
  weight,
  variant,
  color,
  className,
}: {
  weight: KeywordWeight;
  variant?: KeywordChipVariant;
  color?: string;
  className?: string;
}) {
  const tone = keywordChipStyle(weight, null, variant, color);
  return (
    <span
      style={tone.style}
      className={cn(
        "inline-block h-3.5 w-5 rounded-[4px]",
        tone.className,
        className,
      )}
    />
  );
}

export function SlideTag({
  slide,
  onClick,
}: {
  slide: number;
  onClick?: (slide: number) => void;
}) {
  const { t } = useViewerLanguage();
  return (
    <button
      type="button"
      onClick={() => onClick?.(slide)}
      className="rounded px-1.5 py-0.5 text-xs tabular-nums text-[var(--subtle)] ring-1 ring-[var(--border)] transition-colors hover:text-[var(--fg)]"
    >
      {t("Slide {number}", { number: String(slide) })}
    </button>
  );
}
